import { useMemo } from 'react';

import { useMarketDataSnapshot } from '@src/hooks';
import { getMarketDataLevelPrices } from '@src/utils/getMarketDataLevelPrice';
import { EMPTY_ARRAY, toBig, useObservableValue, useSecurity } from '@talos/kyoko';

import { PREDICTION_MARKET_MDS_THROTTLE } from '../types';
import { getPredictionPriceIncrement } from './predictionDisplay';
import { getMidChancePct } from './predictionMath';

export interface UsePredictionOutcomePricesOptions {
  quantity?: string;
  currency?: string;
  baseCurrency?: string;
  conversionRate?: string;
  marketAccounts?: string[];
}

export function usePredictionOutcomePrices(
  symbol: string | undefined,
  subscriptionTag: string,
  options?: UsePredictionOutcomePricesOptions
) {
  const security = useSecurity(symbol);
  const priceIncrement = getPredictionPriceIncrement(security);
  const useQuantityAwareSubscription = options?.quantity != null && options.quantity !== '';

  const marketAccounts = options?.marketAccounts;

  const snapshotParams = useMemo(
    () =>
      symbol
        ? {
            symbol,
            markets: EMPTY_ARRAY,
            marketAccounts,
            depth: useQuantityAwareSubscription ? null : 1,
            priceIncrement,
            // Card qty is contracts. OMS passes QuoteCurrency only when sizing in USD.
            currency: options?.currency,
            baseCurrency: options?.baseCurrency,
            quantity: useQuantityAwareSubscription ? options?.quantity : undefined,
            conversionRate: options?.conversionRate,
            tag: subscriptionTag,
            throttle: PREDICTION_MARKET_MDS_THROTTLE,
          }
        : null,
    [
      symbol,
      subscriptionTag,
      priceIncrement,
      useQuantityAwareSubscription,
      marketAccounts,
      options?.quantity,
      options?.currency,
      options?.baseCurrency,
      options?.conversionRate,
    ]
  );

  const { marketDataSnapshots, marketDataErrors } = useMarketDataSnapshot(snapshotParams);

  const snapshot = useObservableValue(() => marketDataSnapshots, [marketDataSnapshots]);

  return useMemo(() => {
    // useMarketDataSnapshot already filters/clears on symbol change; mirror that here for
    // useObservableValue which can briefly retain the previous emission across remounts.
    const matchingSnapshot = snapshot?.Symbol === symbol ? snapshot : undefined;
    const { bidPrice, offerPrice } = getMarketDataLevelPrices({ snapshot: matchingSnapshot, index: 0 });
    // Each side is validated on its own so a one-sided book still shows and primes the side it has.
    const yesBid = toTradablePrice(bidPrice);
    const yesAsk = toTradablePrice(offerPrice);

    return {
      // Keep the unrounded book mid available as a chance fallback when LastPrice is missing.
      midChancePct: yesBid != null && yesAsk != null ? getMidChancePct(yesBid, yesAsk) : undefined,
      yesAskPrice: yesAsk != null ? offerPrice : undefined,
      yesBidPrice: yesBid != null ? bidPrice : undefined,
      marketDataSnapshots,
      marketDataErrors,
    };
  }, [snapshot, symbol, marketDataSnapshots, marketDataErrors]);
}

/** A book side is tradable only when it is present and a positive Big. */
function toTradablePrice(price: string | undefined): number | undefined {
  const bigPrice = toBig(price);
  return bigPrice != null && bigPrice.gt(0) ? bigPrice.toNumber() : undefined;
}
